import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import Provision from '../../../../../api/provision';
import Table from '../../../../../components/Table';
import useApiQuery from '../../../../../hooks/useApiQuery';

import type { ErrorType } from '@/types';
import type { CaasCluster, Product } from '@/types/productTypes';
import type { TableRowData } from '@/types/tableTypes';

export interface OpenstackClustersProps {
  product: Product;
  setActiveResources: (hasActive: boolean) => void;
}

const OpenstackClusters = ({
  product,
  setActiveResources,
}: OpenstackClustersProps): React.JSX.Element => {
  const navigate = useNavigate();
  const {
    data: clusters = [],
    error: fetchError,
    makeApiCall: getCaasClusters,
  } = useApiQuery<CaasCluster[]>(Provision.fetchCaasClustersV1);

  useEffect(() => {
    if (product.details.juiceGroup) {
      getCaasClusters({ juiceGroup: product.details.juiceGroup, infrastructure: 'openstack' });
    }
  }, [product.details.juiceGroup, getCaasClusters]);

  useEffect(() => {
    if (clusters.length) {
      setActiveResources(
        clusters.some(cluster => ['Pending', 'Approved', 'Provisioned'].includes(cluster.status))
      );
    }
  }, [clusters, setActiveResources]);

  const fields = [
    { id: 'name', title: 'Name' },
    { id: 'version', title: 'Version' },
    { id: 'cloud_id', title: 'Cloud' },
    { id: 'created', title: 'Created At' },
    { id: 'last_updated', title: 'Last Updated' },
  ];

  const rows = clusters.map((cluster: CaasCluster) => ({
    key: `${cluster.name}-${cluster.version}-${cluster.cloud_id}`,
    onRowClick: () => navigate(`/products/${product._id}/caas-clusters/${cluster.name}`),
    data: [
      { field: 'name', value: cluster.name },
      { field: 'version', value: cluster.version },
      { field: 'cloud_id', value: cluster.cloud_id },
      { field: 'created', value: cluster.created },
      { field: 'last_updated', value: cluster.last_updated },
    ],
  })) as TableRowData[];

  return (
    <Table
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
    />
  );
};

export default OpenstackClusters;
